import { useCallback } from "react"
import { DateTime } from "luxon"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDinero } from "../../../../../currency/currencyLib"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { useMoney } from "../../../../../currency/useMoney"

type useExportOrdersToCsvParams = Readonly<{
    data: MeetingServiceOrder[] | null | undefined
    startDate: DateTime
    endDate: DateTime
    includeVatInLabels?: boolean
}>

export function useExportMeetingServiceOrdersToCsv({
    data,
    startDate,
    endDate,
    includeVatInLabels = false,
}: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const vatLabel = includeVatInLabels ? "includes VAT" : ""
    const { l10n } = useAppLocalization()

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const doExport = useCallback(() => {
        const csvOptions = mkConfig({
            fieldSeparator: ";",
            quoteStrings: true,
            decimalSeparator: ".",
            useBom: true,
            useKeysAsHeaders: true,
            filename: `Facilitynet-Orders-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(
                endDate,
            )}`,
        })

        const exportRows =
            data?.flatMap((order) =>
                order.deliveries?.flatMap((delivery) =>
                    delivery.orderLines?.flatMap((line) => ({
                        From: dateTimeFactory.formatDate(startDate),
                        To: dateTimeFactory.formatDate(endDate),
                        "Order Id": order.id,
                        "Delivery time": dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(delivery.deliveryTime)),
                        "Product ID": line.productId,
                        "Product name": line.name,
                        Items: line.items,
                        ["Item price " + `(${getCurrencyString(line.itemPrice)}${vatLabel})`]: getAmountString(
                            line.itemPrice,
                        ),
                        ["Amount " + `(${getCurrencyString(line.price)}${vatLabel})`]: getAmountString(line.price),
                        "Order note": order.orderNote,
                        "Delivery type": order.orderType,
                        "Delivery location": order.deliveryDetails.location.name || "",
                        Sittings: order.deliveryDetails.sittings,
                        "Meeting name": order.meetingDetails.meetingName,
                        // "Meeting host": order
                        "Meeting start time": order.meetingDetails.startDateTime,
                        "Meeting end time": order.meetingDetails.endDateTime,
                        "Creator name": order.customer?.displayName,
                        "Creator email": order.customer?.email,
                        "Creator user group": order.customer?.userGroup?.name ?? "",
                        Company: order.accountingCustomerParty?.name,
                        "Sales location": order.sellerSupplierParty?.name,
                        Channel: order.paymentDetails?.salesChannel,
                    })),
                ),
            ) ?? []

        const csv = generateCsv(csvOptions)(exportRows ?? [])
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, includeVatInLabels])

    return doExport
}
